(() => {
  const callback = () => {
    if (!window.themeComponents.snippets.register('size-guide-ds')) return;

    const { locale } = window.themeComponents.snippets['size-guide-ds'] ?? {};

    const convertCmToInch = ({ value }) => {
      if (value === '') return value;

      return value
        .split(' - ')
        .map((cm) => (!isNaN(cm) ? (cm / 2.54).toFixed(1) : cm))
        .join(' - ');
    };

    const createTableContainer = ({ selectedChart, unit }) => {
      const createAxisLabel = ({ axis, label }) => {
        if (!axis) return;

        const icon =
          "<svg xmlns='http://www.w3.org/2000/svg' width='27' height='9' viewBox='0 0 27 9' fill='none'><path d='M26.3536 4.85355C26.5488 4.65829 26.5488 4.34171 26.3536 4.14645L23.1716 0.964466C22.9763 0.769204 22.6597 0.769204 22.4645 0.964466C22.2692 1.15973 22.2692 1.47631 22.4645 1.67157L25.2929 4.5L22.4645 7.32843C22.2692 7.52369 22.2692 7.84027 22.4645 8.03553C22.6597 8.2308 22.9763 8.2308 23.1716 8.03553L26.3536 4.85355ZM0 5H26V4H0V5Z' fill='#62554A'></path></svg>";

        return label
          ? `<div class="size-guide__table-label size-guide__table-label--${axis}-axis">${label} ${icon}</div>`
          : '';
      };

      const createTable = ({ tableValues, unit }) => {
        if (!tableValues || tableValues.length === 0) return;
        const [headings, ...rows] = tableValues;

        const createRow = ({ tag, cells, unit }) => `
          <tr>
            ${cells
              .map((cell) => `<${tag}>${unit === 'inch' ? convertCmToInch({ value: cell }) : cell}</${tag}>`)
              .join('')}
          </tr>
        `;

        return `
          <table>
            <thead>${createRow({ tag: 'th', cells: headings, unit })}</thead>
            <tbody>${rows.map((row) => createRow({ tag: 'td', cells: row, unit })).join('')}</tbody>
          </table>
        `;
      };

      const { tableValues, xAxisLabel, yAxisLabel } = selectedChart ?? {};
      const xAxisHtml = createAxisLabel({ axis: 'x', label: xAxisLabel });
      const yAxisHtml = createAxisLabel({ axis: 'y', label: yAxisLabel });
      const tableHtml = createTable({ tableValues, unit });

      return `
        <div class="size-guide__table-outer">
          ${xAxisHtml}

          <div class="size-guide__table-inner">
            ${yAxisHtml}
            ${tableHtml}
          </div>
        </div>
      `;
    };

    const createChartSwitch = ({ charts, sectionIndex }) => {
      const inputsHtml = charts
        .map(
          (chart, index) =>
            `<label class="text-label">
              <input type="radio"
              class="sr-only"
              name="size-guide-${sectionIndex}-sizing-system"
              value="${chart.title}"
              ${index === 0 ? 'checked' : ''}
            >
              ${chart.title}
            </label>`,
        )
        .join('');

      return `
        <div class="size-guide__switch size-guide__switch--chart">
          ${inputsHtml}
        </div>
      `;
    };

    const createUnitSwitch = ({ sectionIndex }) => {
      const units = [
        { name: locale.centimeters, value: 'cm' },
        { name: locale.inches, value: 'inch' },
      ];

      const inputsHtml = units
        .map(
          ({ name, value }, index) =>
            `<label>
              <input
                type="radio"
                name="size-guide-${sectionIndex}-unit"
                value="${value}"
                ${index === 0 ? 'checked' : ''}
              >
              ${name}
            </label>`,
        )
        .join('');

      return `
        <div class="size-guide__switch size-guide__switch--unit">
          ${inputsHtml}
        </div>
      `;
    };

    const createInterface = ({ charts, selectedChart, sectionIndex }) => {
      const chartSwitchHtml = createChartSwitch({ charts, sectionIndex });
      const unitSwitchHtml = createUnitSwitch({ sectionIndex });
      const tableContainerHtml = createTableContainer({ selectedChart });

      return `
        <div class="size-guide__toolbar">
          ${chartSwitchHtml}
          ${unitSwitchHtml}
        </div>

        ${tableContainerHtml}
      `;
    };

    const renderContent = ({ container, charts, selectedChart, unit, sectionIndex }) => {
      if (!container) return;

      const isEmptyContainer = container.querySelector(selectors.chartPlaceholder);
      const getContent = isEmptyContainer ? createInterface : createTableContainer;

      container.innerHTML = getContent({
        charts,
        selectedChart,
        unit,
        sectionIndex,
      });
    };

    const initChartHandlers = ({ section, charts, sectionIndex }) => {
      const chartContainer = section.querySelector(selectors.chartContainer);
      if (!chartContainer) return;

      renderContent({
        container: chartContainer,
        charts,
        selectedChart: charts[0],
        sectionIndex,
      });

      const tableWrapper = section.querySelector(selectors.tableWrapper);
      const unitSwitch = section.querySelector(selectors.unitSwitch);
      const chartSwitch = section.querySelector(selectors.chartSwitch);

      chartContainer.addEventListener('change', (event) => {
        if (event.target.closest(selectors.chartSwitch)) {
          const selectedUnit = unitSwitch?.querySelector(selectors.inputChecked)?.value;
          const selectedChart = charts.find((chart) => chart.title === event.target.value);

          unitSwitch?.classList?.toggle('hidden', !selectedChart.isConversionToInchesAllowed);

          renderContent({
            container: tableWrapper,
            selectedChart,
            unit: selectedChart.isConversionToInchesAllowed ? selectedUnit : 'cm',
          });
        }

        if (event.target.closest(selectors.unitSwitch)) {
          const chartSwitchValue = chartSwitch?.querySelector(selectors.inputChecked)?.value;
          const selectedChart = charts.find((chart) => chart.title === chartSwitchValue);

          renderContent({
            container: tableWrapper,
            selectedChart,
            unit: event.target.value,
          });
        }
      });
    };

    const initPopupHandlers = ({ section }) => {
      const popupTabContents = section.querySelectorAll(selectors.popupTabContent);

      section.addEventListener('change', (event) => {
        if (event.target.closest(selectors.popupTabs)) {
          popupTabContents.forEach((item) =>
            item.classList.toggle('active', item.dataset.tabId === event.target.value),
          );
        }
      });
    };

    const setFormFieldsPlaceholders = ({ formFields, selectedChart, firstField, secondField }) => {
      const [, firstTableHeading, secondTableHeading] = selectedChart?.tableValues?.[0] || [];
      const placeholders = selectedChart ? [firstTableHeading, secondTableHeading] : [firstField, secondField];

      formFields.forEach((field, index) => {
        field.placeholder = placeholders[index] || '';
      });
    };

    const findSizeInTableHeadings = ({ tableValues, firstMeasurement, secondMeasurement, unit }) => {
      if (!tableValues || tableValues.length === 0) return;

      const parseValue = ({ value, unit }) => {
        return unit === 'inch' ? convertCmToInch({ value }) : Number(value);
      };

      const matches = firstMeasurement.match(/(\d+)([a-zA-Z]+)/);
      const hasSingleMeasurement = firstMeasurement && !secondMeasurement;
      const firstValue = hasSingleMeasurement ? matches[2] : firstMeasurement;
      const secondValue = hasSingleMeasurement ? Number(matches[1]) : secondMeasurement;

      const [headingRow] = tableValues;
      const columnIndex = headingRow.indexOf(firstValue);

      const rowIndexes = tableValues.reduce((accumulator, row, index) => {
        const isHeadingRow = index === 0;
        if (isHeadingRow) return accumulator;

        const currentRowValue = parseValue({ value: row[0], unit });
        const nextRowValue = parseValue({ value: tableValues[index + 1]?.[0], unit });

        if (currentRowValue === secondValue) {
          accumulator.push(index);
        } else if (currentRowValue < secondValue && secondValue < nextRowValue) {
          accumulator.push(index, index + 1);
        }

        return accumulator;
      }, []);

      const sizeMatches = rowIndexes.map((rowIndex) => tableValues[rowIndex][columnIndex]).filter(Boolean);
      return sizeMatches;
    };

    const findSizeInTableBody = ({ tableValues, firstMeasurement, secondMeasurement, unit }) => {
      if (!tableValues || tableValues.length === 0) return;

      const parseRange = ({ range, unit }) => {
        const [min, max] = range
          .split(' - ')
          .map((value) => (unit === 'inch' ? convertCmToInch({ value }) : Number(value)));

        return { min, max };
      };

      const sortBySize = ({ sizes }) => {
        const sizesOrder = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
        return sizes.sort((a, b) => sizesOrder.indexOf(a) - sizesOrder.indexOf(b));
      };

      const findSizeMatches = ({ tableValues }) => {
        const sizeMatches = tableValues.reduce(
          (accumulator, [size, firstRange, secondRange], index) => {
            const { min: firstRangeMin, max: firstRangeMax } = parseRange({ range: firstRange, unit });
            const { min: secondRangeMin, max: secondRangeMax } = parseRange({ range: secondRange, unit });

            if (firstMeasurement >= firstRangeMin && firstMeasurement <= firstRangeMax) {
              accumulator.firstMatches.push({ size, index });
            }

            if (secondMeasurement >= secondRangeMin && secondMeasurement <= secondRangeMax) {
              accumulator.secondMatches.push({ size, index });
            }

            return accumulator;
          },
          { firstMatches: [], secondMatches: [] },
        );

        return sizeMatches;
      };

      const getConsecutiveMatches = ({ sizeMatches }) => {
        const { firstMatches, secondMatches } = sizeMatches;
        const sizes = [];

        firstMatches.forEach((firstMatch) => {
          const match = secondMatches.find((secondMatch) => {
            const isSameSize = firstMatch.size === secondMatch.size;
            const isConsecutiveOrder = Math.abs(firstMatch.index - secondMatch.index) <= 1;

            return isSameSize && isConsecutiveOrder;
          });

          if (match) sizes.push(firstMatch.size);
        });

        return sortBySize({ sizes });
      };

      const sizeMatches = findSizeMatches({ tableValues });
      const consecutiveMatches = getConsecutiveMatches({ sizeMatches });

      return consecutiveMatches;
    };

    const renderSearchResults = ({ container, matchedSizes, matchedVariants }) => {
      if (!container) return;

      const noSizeHtml = `
        <p class="size-guide__popup-calculator-result-heading">${locale.noSize}</p>
        <p class="size-guide__popup-calculator-result-heading">${locale.contactUs}</p>
      `;

      if (!matchedSizes) {
        container.innerHTML = noSizeHtml;
        return;
      }

      const { sizingSuggestion } = container.dataset;
      const sizingSuggestionHtml =
        matchedSizes.length > 1 && sizingSuggestion
          ? `<p class="size-guide__popup-calculator-result-info">${sizingSuggestion}</p>`
          : '';

      const addToCartHtml =
        matchedVariants && matchedVariants.length === 1 && matchedVariants[0].available
          ? `<button type="button" class="size-guide__popup-calculator-result-button btn btn--minimalist btn--small" data-variant-id="${matchedVariants[0].id}">${locale.addToCart}</button>`
          : '';

      const foundSizeHtml = `
        <p class="size-guide__popup-calculator-result-heading">${locale.yourSizeIs}</p>
        <p class="size-guide__popup-calculator-result-size">${matchedSizes.join(` ${locale.or} `)}</p>
        ${sizingSuggestionHtml}
        ${addToCartHtml}
      `;

      container.innerHTML = matchedSizes.length ? foundSizeHtml : noSizeHtml;
    };

    const initCalculatorHandlers = ({ section, charts }) => {
      const chartContainer = section.querySelector(selectors.chartContainer);
      const calculatorForm = section.querySelector(selectors.calculatorForm);
      const calculatorResult = section.querySelector(selectors.calculatorResult);
      const unitSwitch = calculatorForm.querySelector(selectors.unitSwitch);
      const hasMissingElements = [chartContainer, calculatorForm, calculatorResult, unitSwitch].some(
        (element) => !element,
      );
      if (hasMissingElements) return;

      const variantsJson = document.querySelector('[data-variant-json]');
      const variants = JSON.parse(variantsJson?.innerHTML) ?? [];

      const chartSwitchHtml = createChartSwitch({ charts, sectionIndex: 'calculator' });
      calculatorForm.insertAdjacentHTML('afterbegin', chartSwitchHtml);

      const formFields = calculatorForm.querySelectorAll(selectors.calculatorFormField);

      setFormFieldsPlaceholders({ formFields, selectedChart: charts[0] });

      formFields.forEach((field) => {
        field.addEventListener('input', (event) => {
          let inputValue = event.target.value;

          inputValue = inputValue.replace(/,/g, '.');
          inputValue = inputValue.replace(/[^a-zA-Z0-9,.]/g, '');
          inputValue = inputValue.toUpperCase();

          event.target.value = inputValue;
        });
      });

      calculatorForm.addEventListener('change', (event) => {
        if (!event.target.closest(selectors.chartSwitch)) return;

        const selectedChart = charts.find((chart) => chart.title === event.target.value);

        const [, secondFormField] = formFields || [];
        if (secondFormField) secondFormField.disabled = !selectedChart.isPrimaryChart;

        setFormFieldsPlaceholders({
          formFields,
          ...(selectedChart.isPrimaryChart ? { selectedChart } : { firstField: locale.enterCupSize, secondField: '' }),
        });

        unitSwitch.classList.toggle('hidden', !selectedChart.isConversionToInchesAllowed);
      });

      calculatorForm.addEventListener('submit', (event) => {
        event.preventDefault();

        const formData = new FormData(calculatorForm);
        const {
          first,
          second,
          'size-guide-calculator-sizing-system': sizingSystem,
          'size-guide-calculator-unit': unit,
        } = Object.fromEntries(formData.entries());

        const selectedChart = charts.find((chart) => chart.title === sizingSystem);
        const hasAxisLabels = !!(selectedChart.xAxisLabel || selectedChart.yAxisLabel);
        const findSize = hasAxisLabels ? findSizeInTableHeadings : findSizeInTableBody;

        const matchedSizes = findSize({
          tableValues: selectedChart.tableValues,
          firstMeasurement: first,
          secondMeasurement: second,
          ...(selectedChart.isConversionToInchesAllowed && { unit }),
        });

        const matchedVariants = matchedSizes.map((size) =>
          variants.find((variant) => variant.options.some((option) => option === size)),
        );

        renderSearchResults({ container: calculatorResult, matchedSizes, matchedVariants });
      });

      calculatorResult.addEventListener('click', (event) => {
        if (!event.target.closest(selectors.calculatorResultButton)) return;

        const { variantId } = event.target.dataset;
        if (variantId) window.addToCart(variantId, 1);

        event.target.closest(selectors.dialog).close();
      });
    };

    const getCharts = ({ section }) => {
      const isValidJson = ({ string }) => {
        try {
          JSON.parse(string);
        } catch (error) {
          return false;
        }
        return true;
      };

      const mapTableRows = ({ tableRows }) => {
        const mapRow = (row) => row.split('|').map(mapCell);
        const mapCell = (cell) => (cell === '-' ? '' : cell);

        return (tableRows ?? []).map(mapRow);
      };

      const jsonString = section.querySelector(selectors.chartsJson)?.textContent;
      if (!isValidJson({ string: jsonString })) return;

      const charts = JSON.parse(jsonString);

      return charts.map((chart, index) => {
        const {
          title,
          x_axis_label: xAxisLabel,
          y_axis_label: yAxisLabel,
          table_rows: tableRows,
          is_conversion_to_inches_allowed: isConversionToInchesAllowed,
        } = chart;

        return {
          title,
          ...(xAxisLabel && { xAxisLabel }),
          ...(yAxisLabel && { yAxisLabel }),
          tableValues: mapTableRows({ tableRows }),
          isConversionToInchesAllowed: !!isConversionToInchesAllowed,
          isPrimaryChart: !index,
        };
      });
    };

    const selectors = {
      section: '.size-guide',
      chartsJson: '[data-size-charts-json]',
      chartContainer: '.size-guide__chart-container',
      chartPlaceholder: '.size-guide__chart-placeholder',
      tableWrapper: '.size-guide__table-outer',
      chartSwitch: '.size-guide__switch--chart',
      unitSwitch: '.size-guide__switch--unit',
      calculatorForm: '#size-calculator',
      calculatorFormField: '.size-guide__popup-calculator-field',
      calculatorResult: '.size-guide__popup-calculator-result',
      calculatorResultButton: '.size-guide__popup-calculator-result-button',
      popupTabs: '.size-guide__popup-tabs',
      popupTabContent: '.size-guide__popup-tab-content',
      inputChecked: 'input:checked',
      dialog: 'dialog',
    };

    const sections = document.querySelectorAll(selectors.section);
    sections.forEach((section, index) => {
      const charts = getCharts({ section });
      if (!charts || charts.length === 0) return;

      initChartHandlers({ section, charts, sectionIndex: index });

      if (section.dataset.type === 'popup') {
        initPopupHandlers({ section });
        initCalculatorHandlers({ section, charts });
      }
    });

    document.addEventListener('shopify:section:load', (event) => {
      const section = event.target.querySelector(selectors.section);
      if (!section) return;

      const charts = getCharts({ section });
      if (!charts || charts.length === 0) return;

      initChartHandlers({ section, charts });
    });
  };

  window.addEventListener('DOMContentLoaded', callback);
})();
